<template>
  <div class="relative">
    <!-- Main Image -->
    <div class="relative h-[400px] overflow-hidden rounded-xl">
      <img
        :src="currentImage"
        :alt="`Slide ${currentSlide + 1}`"
        class="w-full h-full object-cover transition-opacity duration-300"
      />
    </div>

    <!-- Prev/Next Buttons for Main Image -->
    <button
      type="button"
      v-if="hasMultipleImages"
      @click="prevSlide"
      class="absolute left-4 top-[41%] transform -translate-y-1/2 p-2 rounded-full bg-white/30 hover:bg-white/50 shadow-lg"
    >
      <span class="text-white"><b>&lt;</b></span>
    </button>

    <button
      type="button"
      v-if="hasMultipleImages"
      @click="nextSlide"
      class="absolute right-4 top-[41%] transform -translate-y-1/2 p-2 rounded-full bg-white/30 hover:bg-white/50 shadow-lg"
    >
      <span class="text-white"><b>&gt;</b></span>
    </button>

    <!-- Thumbnail Slider (Draggable) -->
    <div class="mt-4 w-full relative overflow-hidden" v-if="shouldShowThumbnails">
      <div
        ref="thumbnailContainer"
        class="flex gap-2 overflow-x-auto scrollbar-hide snap-x snap-mandatory"
        @mousedown="startDrag"
        @mousemove="onDrag"
        @mouseup="stopDrag"
        @mouseleave="stopDrag"
      >
        <div
          v-for="(image, index) in safeImages"
          :key="index"
          @click="goToSlide(index)"
          class="thumbnail-item w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden cursor-pointer snap-start"
          :class="{ 'ring-2 ring-blue-500': currentSlide === index }"
        >
          <img
            :src="getImageUrl(image)"
            :alt="`Thumbnail ${index + 1}`"
            class="w-full h-full object-cover"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { API_URL } from "../constants";

const props = defineProps({
  images: {
    type: Array,
    default: () => [],
  },
  baseUrl: {
    type: String,
    default: API_URL,
  },
  imageWiseSlide: {
    type: Boolean,
    default: false,
  },
});
const defaultImage = `${props.baseUrl}/images/image-not-found.jpg`;
const currentSlide = ref(0);
const isDragging = ref(false);
const startX = ref(0);
const scrollLeft = ref(0);
const thumbnailContainer = ref(null);

// Computed properties
const safeImages = computed(() => (Array.isArray(props.images) ? props.images : []));
const hasMultipleImages = computed(() => safeImages.value.length > 1);
const currentImage = computed(() => safeImages.value.length ? getImageUrl(safeImages.value[currentSlide.value]) : defaultImage);

// Watch for image changes
watch(() => props.images, () => {
  currentSlide.value = 0;
}, { deep: true });

// Helper function to get image URLs
const getImageUrl = (image) => image || defaultImage;

// Main Image Navigation
const prevSlide = () => {
  if (!hasMultipleImages.value) return;
  currentSlide.value = currentSlide.value === 0 ? safeImages.value.length - 1 : currentSlide.value - 1;
};

const nextSlide = () => {
  if (!hasMultipleImages.value) return;
  currentSlide.value = currentSlide.value === safeImages.value.length - 1 ? 0 : currentSlide.value + 1;
};
const shouldShowThumbnails = computed(() => {
  return props.imageWiseSlide && safeImages.value.length > 0;
});


// Click on a Thumbnail
const goToSlide = (index) => {
  if (index >= 0 && index < safeImages.value.length) {
    currentSlide.value = index;
  }
};

// Drag to Scroll Functionality
const startDrag = (event) => {
  isDragging.value = true;
  startX.value = event.pageX - thumbnailContainer.value.offsetLeft;
  scrollLeft.value = thumbnailContainer.value.scrollLeft;
};

const onDrag = (event) => {
  if (!isDragging.value) return;
  event.preventDefault();
  const x = event.pageX - thumbnailContainer.value.offsetLeft;
  const walk = (x - startX.value) * 2; // Scroll speed
  thumbnailContainer.value.scrollLeft = scrollLeft.value - walk;
};

const stopDrag = () => {
  isDragging.value = false;
};
</script>

<style scoped>
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.thumbnail-item {
  transition: transform 0.2s ease-in-out;
}

.thumbnail-item:hover {
  transform: scale(1.05);
}
</style>
